import { t } from 'i18next';
import { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Divider,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Add, CheckBox as CheckBoxIcon, CheckBoxOutlineBlank, WhatsApp, Sync } from '@mui/icons-material';
import { useQueryClient } from 'react-query';

import { StepOptionsResponseDto, TeamResponseDto, WorkflowResponseDto } from '@bandido/shared/dtos';

import { useGetClientById } from '@frontend/features/clients/queries';
import { useGetAllUsers, useGetAgents } from '@frontend/features/users/queries';
import { useGetAllTeams } from '@frontend/features/teams/queries';
import { useGetAllWorkflowsQuery, useGetStepsByManyWorkflowIds } from '@frontend/features/workflows/queries';
import { useCases } from '@frontend/features/cases/components/CaseContext';
import { useGetAllCases } from '@frontend/features/cases/queries';
import { DEFAULT_PAGE, ROWS_PER_PAGE_OPTIONS, Table } from '@frontend/features/common/components/Table';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { BackButton } from '@frontend/features/common/components/BackButton';
import { ChipCustom } from '@frontend/features/common/components/ChipCustom';
import { Link } from '@frontend/features/common/components/Link';
import { addLeadingZeros, getContrastYIQ } from '@frontend/features/common/utils/helpers';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { getArrayParam, getTimeSinceDate, updateSearchParams } from '@frontend/features/common/helper';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { countries } from '@frontend/features/common/constants/countries';
import { CountryFlag } from '@frontend/features/common/components/CountryFlag';
import { DocumentType } from '@frontend/features/auth/types';

import { ClientViewMobile } from '@frontend/features/clients/components/ClientViewMobile';
import { routes } from '../routes';
import { useWhatsappClient } from '@frontend/features/whatsapp/components/WhatsappClientContext';
import { QueryKeys } from '@frontend/features/common/types';

export const ClientView = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();

  const [searchParams, setSearchParams] = useSearchParams();
  const rawPage = Number(searchParams.get('page'));
  const rawPageSize = Number(searchParams.get('pageSize'));
  const pagination = {
    page: Number.isInteger(rawPage) && rawPage > 0 ? rawPage : DEFAULT_PAGE,
    rowsPerPage: ROWS_PER_PAGE_OPTIONS.includes(rawPageSize) ? rawPageSize : ROWS_PER_PAGE_OPTIONS[0],
    handlePagination: (page: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('page', String(page));
      setSearchParams(next, { replace: true });
    },
    handleRowsPerPage: (pageSize: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('page', '1');
      next.set('pageSize', String(pageSize));
      setSearchParams(next, { replace: true });
    },
  };
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams();
  const { data, isLoading, isError, refetch } = useGetClientById({
    id,
  });
  const navigate = useNavigate();
  const { toggleModal } = useCases();

  const [teamSearch, setTeamSearch] = useState('');
  const [workflowSearch, setWorkflowSearch] = useState('');
  const [stepSearch, setStepSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const search = searchParams.get('search') || '';
  const updateCaseFilters = (values: Record<string, string>) => updateSearchParams(searchParams, setSearchParams, { ...values, page: '1' });
  const setSearch = (value: string) => updateCaseFilters({ search: value });

  // Form states for each block
  const [basicInfo, setBasicInfo] = useState({
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
  });

  const [contactInfo, setContactInfo] = useState({
    email: data?.email || '',
    phoneCountryCode: data?.phoneCountryCode || '',
    phoneNumber: data?.phoneNumber || '',
    whatsappNumber: data?.whatsappNumber || '',
  });

  const [addressInfo, setAddressInfo] = useState({
    country: data?.country || '',
    city: data?.city || '',
    postalCode: data?.postalCode || '',
    address1: data?.address1 || '',
    address2: data?.address2 || '',
  });

  const [documentInfo, setDocumentInfo] = useState({
    documentType: data?.documentType || DocumentType.CI,
    documentNumber: data?.documentNumber || '',
  });

  const [preferredAgentInfo, setPreferredAgentInfo] = useState({
    preferredAgentId: data?.preferredAgentId || '',
  });

  const { data: agents } = useGetAgents({
    pageSize: 1000,
    currentPage: 1,
  });

  // Update form states when data changes
  useEffect(() => {
    if (data) {
      setBasicInfo({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
      });
      setContactInfo({
        email: data.email || '',
        phoneCountryCode: data.phoneCountryCode || '',
        phoneNumber: data.phoneNumber || '',
        whatsappNumber: data.whatsappNumber || '',
      });
      setAddressInfo({
        country: data.country || '',
        city: data.city || '',
        postalCode: data.postalCode || '',
        address1: data.address1 || '',
        address2: data.address2 || '',
      });
      setDocumentInfo({
        documentType: data.documentType || DocumentType.CI,
        documentNumber: data.documentNumber || '',
      });
      setPreferredAgentInfo({
        preferredAgentId: data.preferredAgentId || '',
      });
    }
  }, [data]);

  // Save handlers for each block
  const handleSaveBasicInfo = async () => {
    try {
      await client.patch(`/client/${id}`, {
        ...basicInfo,
        agency: data?.agencyId?.toString(),
      });
      addToast({
        message: t('clients.view.basicInfoSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries([QueryKeys.getClientById, id]);
    } catch (e) {
      addToast({
        message: t('clients.view.createdError'),
        color: 'error',
      });
    }
  };

  const handleSaveContactInfo = async () => {
    try {
      await client.patch(`/client/${id}`, {
        ...contactInfo,
        agency: data?.agencyId?.toString(),
      });
      addToast({
        message: t('clients.view.contactInfoSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries([QueryKeys.getClientById, id]);
    } catch (e) {
      addToast({
        message: t('clients.view.createdError'),
        color: 'error',
      });
    }
  };

  const handleSaveAddressInfo = async () => {
    try {
      await client.patch(`/client/${id}`, {
        ...addressInfo,
        agency: data?.agencyId?.toString(),
      });
      addToast({
        message: t('clients.view.addressInfoSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries([QueryKeys.getClientById, id]);
    } catch (e) {
      addToast({
        message: t('clients.view.createdError'),
        color: 'error',
      });
    }
  };

  const handleSaveDocumentInfo = async () => {
    try {
      await client.patch(`/client/${id}`, {
        ...documentInfo,
        agency: data?.agencyId?.toString(),
      });
      addToast({
        message: t('clients.view.documentInfoSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries([QueryKeys.getClientById, id]);
    } catch (e) {
      addToast({
        message: t('clients.view.createdError'),
        color: 'error',
      });
    }
  };

  const handleSavePreferredAgent = async () => {
    try {
      await client.patch(`/client/${id}`, {
        ...preferredAgentInfo,
        agency: data?.agencyId?.toString(),
      });
      addToast({
        message: t('clients.view.preferredAgentSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries([QueryKeys.getClientById, id]);
    } catch (e) {
      addToast({
        message: t('clients.view.createdError'),
        color: 'error',
      });
    }
  };

  const teamIdsVal = searchParams.get('teamIds');
  const workflowIds = searchParams.get('workflowIds');
  const stepIds = searchParams.get('stepIds');
  const agentIds = searchParams.get('agentIds');

  const { openWhatsapp } = useWhatsappClient();

  const {
    data: casesData, isLoading: casesLoading, isFetching: casesFetching,
    isPreviousData: casesPrevious, isError: casesError, refetch: refetchCases,
  } = useGetAllCases({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
    teamIds: getArrayParam(teamIdsVal)?.filter(v => v !== null).map((i) => 
      i === 'notselected' ? 'notselected' : Number(i)
    ) as any,
    workflowIds: getArrayParam(workflowIds),
    stepIds: getArrayParam(stepIds),
    clientIds: id ? [id] : [],
    agentIds: getArrayParam(agentIds),
  });

  const { data: agentList } = useGetAllUsers({
    pageSize: 10,
    currentPage: 1,
    search: agentSearch,
  });

  const { data: teams } = useGetAllTeams({
    pageSize: 10000,
    currentPage: 1,
    search: teamSearch,
  });

  const { data: workflows } = useGetAllWorkflowsQuery({
    pageSize: 10000,
    currentPage: 1,
    search: workflowSearch,
  });

  const { data: steps } = useGetStepsByManyWorkflowIds({
    search: stepSearch,
    workflowIds: searchParams.get('workflowIds')?.split('|') || [],
  });

  const createadSince = data?.createdAt ? getTimeSinceDate(String(data?.createdAt)) : '-';


  const caseFilters = (
    <Box display="flex" width="100%" gap={isMobile ? 2 : 4} flexDirection={isMobile ? 'column' : 'row'}>
      <Autocomplete
        fullWidth
        limitTags={1}
        size="small"
        multiple
        options={teams?.items || []}
        getOptionLabel={(option) => option.name}
        value={
          teams?.items.filter(
            (option) => searchParams.get('teamIds')?.split('|')?.includes(String(option.id)),
          ) || []
        }
        onChange={(_event, value: TeamResponseDto[]) => {
          const valuesForUrl = value.map((val) => val.id).join('|');
          updateCaseFilters({ teamIds: valuesForUrl });
        }}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" label={t('cases.list.filterByTeam')} fullWidth />
        )}
        renderOption={(props, option, { selected }) => {
          const { ...optionProps } = props;
          return (
            <li {...optionProps} key={option.id}>
              <Checkbox
                icon={<CheckBoxOutlineBlank fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                sx={{ mr: 1 }}
                checked={selected}
              />
              {option.name}
            </li>
          );
        }}
        inputValue={teamSearch}
        onInputChange={(_event, value) => {
          setTeamSearch(value);
        }}
      />

      <Autocomplete
        fullWidth
        limitTags={1}
        size="small"
        multiple
        options={workflows?.items || []}
        getOptionLabel={(option) => option.name}
        value={
          workflows?.items.filter(
            (option) => searchParams.get('workflowIds')?.split('|')?.includes(String(option.id)),
          ) || []
        }
        onChange={(_event, value: WorkflowResponseDto[]) => {
          const valuesForUrl = value.map((val) => val.id).join('|');
          const params: Record<string, string> = { workflowIds: valuesForUrl };

          if (!value.length) {
            params.stepIds = '';
          }

          updateCaseFilters(params);
        }}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" label={t('cases.list.filterByWorkflow')} fullWidth />
        )}
        renderOption={(props, option, { selected }) => {
          const { ...optionProps } = props;
          return (
            <li {...optionProps} key={option.id}>
              <Checkbox
                icon={<CheckBoxOutlineBlank fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                sx={{ mr: 1 }}
                checked={selected}
              />
              {option.name}
            </li>
          );
        }}
        inputValue={workflowSearch}
        onInputChange={(_event, value) => {
          setWorkflowSearch(value);
        }}
      />

      <Autocomplete
        fullWidth
        disabled={!searchParams.get('workflowIds')}
        limitTags={1}
        size="small"
        multiple
        options={steps || []}
        getOptionLabel={(option) => option.name}
        value={
          steps?.filter((option) => searchParams.get('stepIds')?.split('|')?.includes(String(option.id))) ||
                    []
        }
        onChange={(_event, value: StepOptionsResponseDto[]) => {
          const valuesForUrl = value.map((val) => val.id).join('|');
          updateCaseFilters({ stepIds: valuesForUrl });
        }}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" label={t('cases.list.filterByStep')} fullWidth />
        )}
        renderOption={(props, option, { selected }) => {
          const { ...optionProps } = props;
          return (
            <li {...optionProps} key={option.id}>
              <Checkbox
                icon={<CheckBoxOutlineBlank fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                sx={{ mr: 1 }}
                checked={selected}
              />
              {option.name}
            </li>
          );
        }}
        inputValue={stepSearch}
        onInputChange={(_event, value) => {
          setStepSearch(value);
        }}
      />

      <Autocomplete
        fullWidth
        limitTags={1}
        size="small"
        multiple
        options={agentList?.items || []}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
        value={
          agentList?.items.filter(
            (option) => searchParams.get('agentIds')?.split('|')?.includes(String(option.id)),
          ) || []
        }
        onChange={(_event, value) => {
          const valuesForUrl = value.map((val) => val.id).join('|');
          updateCaseFilters({ agentIds: valuesForUrl });
        }}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" label={t('cases.list.filterByAgent')} fullWidth />
        )}
        renderOption={(props, option, { selected }) => {
          const { ...optionProps } = props;
          return (
            <li {...optionProps} key={option.id}>
              <Checkbox
                icon={<CheckBoxOutlineBlank fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                sx={{ mr: 1 }}
                checked={selected}
              />
              {option.firstName} {option.lastName}
            </li>
          );
        }}
        inputValue={agentSearch}
        onInputChange={(_event, value) => {
          setAgentSearch(value);
        }}
      />
    </Box>
  );
  const clientInfo = (
    <Card
      sx={{
        p: theme.spacing(3),
        boxShadow: 'none',
        borderRadius: theme.spacing(3),
      }}
      elevation={4}
    >
      <Typography variant="h6" gutterBottom>
        {t('clients.view.title')}
      </Typography>

      <Box display="flex" flexDirection="column" gap={3}>
        {/* Agency Info - Read Only */}
        <Box>
          <Typography variant="subtitle2" mb={2} gutterBottom>
            {t('clients.view.agencyInfo')}
          </Typography>
          <TextField
            fullWidth
            disabled
            size="small"
            value={data?.agencyName}
            label={t('clients.view.agency')}
          />
        </Box>

        {/* Preferred Agent Block */}
        <Box>
          <Typography variant="subtitle2" mb={2} gutterBottom>
            {t('clients.view.preferredAgent')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <Autocomplete
              fullWidth
              size="small"
              options={agents?.items || []}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              value={agents?.items.find(agent => agent.id === preferredAgentInfo.preferredAgentId) || null}
              onChange={(_event, value) => setPreferredAgentInfo({
                preferredAgentId: value?.id || ''
              })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('clients.view.selectPreferredAgent')}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.firstName} {option.lastName}
                </li>
              )}
            />
            <Button
              variant="text"
              size="small"
              startIcon={<Sync fontSize="small" />}
              onClick={handleSavePreferredAgent}
              sx={{ alignSelf: 'flex-end' }}
            >
              {t('common.update')}
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Basic Info Block */}
        <Box>
          <Typography variant="subtitle2" mb={2} gutterBottom>
            {t('clients.view.basicInfo')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              size="small"
              value={basicInfo.firstName}
              label={t('clients.view.firstName')}
              onChange={(e) => setBasicInfo({ ...basicInfo, firstName: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              value={basicInfo.lastName}
              label={t('clients.view.lastName')}
              onChange={(e) => setBasicInfo({ ...basicInfo, lastName: e.target.value })}
            />
            <Button
              variant="text"
              size="small"
              startIcon={<Sync fontSize="small" />}
              onClick={handleSaveBasicInfo}
              sx={{ alignSelf: 'flex-end' }}
            >
              {t('common.update')}
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Contact Info Block */}
        <Box>
          <Typography variant="subtitle2" mb={2} gutterBottom>
            {t('clients.view.contactInfo')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              size="small"
              value={contactInfo.email}
              label={t('clients.view.email')}
              onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
            />
            <Box className="client-form-row" display="flex" gap={2}>
              <TextField
                select
                fullWidth
                size="small"
                value={contactInfo.phoneCountryCode}
                label={t('clients.view.phoneCountryCode')}
                onChange={(e) => setContactInfo({ ...contactInfo, phoneCountryCode: e.target.value })}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.phoneCode}>
                    <CountryFlag countryCode={country.code} />
                    {country.code} (+{country.phoneCode})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                value={contactInfo.phoneNumber}
                label={t('clients.view.phoneNumber')}
                onChange={(e) => setContactInfo({ ...contactInfo, phoneNumber: e.target.value })}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              value={contactInfo.whatsappNumber}
              label={t('clients.view.whatsappNumber')}
              inputProps={{ inputMode: 'numeric' }}
              onChange={(e) => setContactInfo({ ...contactInfo, whatsappNumber: e.target.value.replace(/\D/g, '') })}
            />
            <Button
              variant="text"
              size="small"
              startIcon={<Sync fontSize="small" />}
              onClick={handleSaveContactInfo}
              sx={{ alignSelf: 'flex-end' }}
            >
              {t('common.update')}
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Address Info Block */}
        <Box>
          <Typography variant="subtitle2" mb={2} gutterBottom>
            {t('clients.view.addressInfo')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              select
              fullWidth
              size="small"
              value={addressInfo.country}
              label={t('clients.view.country')}
              onChange={(e) => setAddressInfo({ ...addressInfo, country: e.target.value })}
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.name}>
                  <CountryFlag countryCode={country.code} />
                  {country.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              value={addressInfo.city}
              label={t('clients.view.city')}
              onChange={(e) => setAddressInfo({ ...addressInfo, city: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              value={addressInfo.postalCode}
              label={t('clients.view.postalCode')}
              onChange={(e) => setAddressInfo({ ...addressInfo, postalCode: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              value={addressInfo.address1}
              label={t('clients.view.address1')}
              onChange={(e) => setAddressInfo({ ...addressInfo, address1: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              value={addressInfo.address2}
              label={t('clients.view.address2')}
              onChange={(e) => setAddressInfo({ ...addressInfo, address2: e.target.value })}
            />
            <Button
              variant="text"
              size="small"
              startIcon={<Sync fontSize="small" />}
              onClick={handleSaveAddressInfo}
              sx={{ alignSelf: 'flex-end' }}
            >
              {t('common.update')}
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Document Info Block */}
        <Box>
          <Typography variant="subtitle2" mb={2} gutterBottom>
            {t('clients.view.documentInfo')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box className="client-form-row" display="flex" gap={2}>
              <TextField
                select
                fullWidth
                size="small"
                value={documentInfo.documentType}
                label={t('clients.view.documentType')}
                onChange={(e) => setDocumentInfo({ ...documentInfo, documentType: e.target.value })}
              >
                <MenuItem value={DocumentType.CI}>{DocumentType.CI}</MenuItem>
                <MenuItem value={DocumentType.DNI}>{DocumentType.DNI}</MenuItem>
                <MenuItem value={DocumentType.PASSPORT}>{DocumentType.PASSPORT}</MenuItem>
              </TextField>
              <TextField
                fullWidth
                size="small"
                value={documentInfo.documentNumber}
                label={t('clients.view.documentNumber')}
                onChange={(e) => setDocumentInfo({ ...documentInfo, documentNumber: e.target.value })}
              />
            </Box>
            <Button
              variant="text"
              size="small"
              startIcon={<Sync fontSize="small" />}
              onClick={handleSaveDocumentInfo}
              sx={{ alignSelf: 'flex-end' }}
            >
              {t('common.update')}
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Client Since - Read Only */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="textSecondary">
            {t('clients.view.clientSince')}:
          </Typography>
          <Typography variant="body2">
            {createadSince}
          </Typography>
        </Box>
      </Box>
    </Card>
  );

  if (isMobile) return <ClientViewMobile client={data} isLoading={isLoading} isError={isError} onRetry={() => refetch()}
    cases={casesData?.items || []} totalCases={casesData?.count || 0} casesLoading={casesLoading} casesFetching={casesFetching}
    casesPrevious={casesPrevious} casesError={casesError} onRetryCases={() => refetchCases()} onCreateCase={() => toggleModal(id)}
    pagination={pagination} search={search} onSearch={setSearch} filters={caseFilters} editor={clientInfo} />;

  return (
    <Box display="flex" gap={4} flexDirection="column" p={theme.spacing(4)}>
      <BackButton backRoute={routes.dashboard.clients} />

      <Box>
        <Box display="flex" gap={2} alignItems="center">
          <Typography variant="h6" color={theme.palette.text.primary}>
            {data?.firstName || ''} {data?.lastName || ''}
          </Typography>
            <IconButton onClick={() => openWhatsapp(id)}><WhatsApp /></IconButton>
        </Box>
        <Typography variant="subtitle1" align="left" color={theme.palette.text.disabled}>
          {data?.email}
        </Typography>
      </Box>

      <Grid container>
        <Grid item xs={12} sm={8}>
          <Card
            elevation={4}
            sx={{
              p: theme.spacing(3),
              boxShadow: 'none',
              borderRadius: theme.spacing(3),
            }}
          >
            <Box marginBottom={3} display="flex" flexDirection="column" gap={3}>
              <Box display="flex" alignItems="center" gap={theme.spacing(3)}>
                <Typography
                  variant="subtitle1"
                  align="left"
                  color={theme.palette.text.primary}
                  sx={{
                    flexGrow: 1,
                  }}
                >
                  {casesData?.count} {t('cases.list.results')}
                </Typography>

                <ToggleButtonGroup
                  exclusive
                  sx={{
                    height: theme.spacing(5),
                    '& button': {
                      padding: theme.spacing(1),
                      width: theme.spacing(5),
                    },
                  }}
                >
                  <ToggleButton onClick={() => toggleModal(id)} value="add" aria-label="Add case">
                    <Add />
                  </ToggleButton>
                </ToggleButtonGroup>
                <SearchInput
                  placeholder={t('cases.list.search')}
                  value={search}
                  onSearch={(value: string) => {
                    setSearch(value);
                  }}
                />
              </Box>

              {caseFilters}

              <Table
                columns={[
                  {
                    id: 'id',
                    label: 'id',
                    align: 'left',
                    format: (data: number) => (
                      <Link to={routes.dashboard.caseView.replace(':id', String(data))}>
                        {addLeadingZeros(data, defaultLeadingZeros)}
                      </Link>
                    ),
                  },
                  {
                    id: 'clientFullName',
                    label: t('cases.list.table.client'),
                    align: 'left',
                  },
                  {
                    id: 'subject',
                    label: t('cases.list.table.subject'),
                    align: 'left',
                  },
                  {
                    id: 'agencyName',
                    label: t('cases.list.table.agency'),
                    align: 'left',
                  },
                  {
                    id: 'workflow',
                    label: t('cases.list.table.workflow'),
                    align: 'left',
                  },
                  {
                    id: 'step',
                    label: t('cases.list.table.step'),
                    align: 'left',
                  },
                  {
                    id: 'team',
                    label: t('cases.list.table.team'),
                    align: 'left',
                  },

                  {
                    id: 'status',
                    label: t('cases.list.table.status'),
                    align: 'left',
                  },
                  {
                    id: 'agent',
                    label: t('cases.list.table.agent'),
                    align: 'left',
                  },
                  {
                    id: 'lastUpdated',
                    label: t('cases.list.table.time'),
                    align: 'left',
                  },
                ]}
                data={{
                  key: 'id',
                  items:
                    casesData?.items.map((dbItem) => ({
                      clientFullName: dbItem.clientFirstName ? (
                        <Link to={routes.dashboard.clientView.replace(':id', String(dbItem.clientId))}>
                          {`${dbItem.clientFirstName} ${dbItem.clientLastName}`}
                        </Link>
                      ) : (
                        t('common.na')
                      ),
                      id: dbItem.id,
                      subject: dbItem.name,
                      agencyName: dbItem.agencyName ? (
                        <Link to={routes.dashboard.agencyView.root.replace(':id', String(dbItem.agencyId))}>
                          {dbItem.agencyName}
                        </Link>
                      ) : (
                        t('common.unassigned')
                      ),
                      team: dbItem.teamId ? (
                        <ChipCustom
                          onClick={() =>
                            navigate(
                              `${routes.dashboard.agencyView.root.replace(
                                ':id',
                                String(dbItem.agencyId),
                              )}/${routes.dashboard.agencyView.team.replace(':teamId', String(dbItem.teamId))}`,
                            )
                          }
                          label={dbItem.teamName}
                          backgroundColor={dbItem.teamColor}
                          textColor={getContrastYIQ(theme, dbItem.teamColor)}
                          component="span"
                        />
                      ) : (
                        <Chip label={t('common.unassigned')} component="span" />
                      ),

                      workflow: dbItem.workflowId ? (
                        <ChipCustom
                          label={dbItem.workflowName}
                          backgroundColor={dbItem.workflowColor}
                          textColor={getContrastYIQ(theme, dbItem.workflowColor)}
                          component="span"
                        />
                      ) : (
                        <Chip label={t('common.unassigned')} component="span" />
                      ),
                      step: dbItem.stepId ? (
                        <ChipCustom
                          label={dbItem.stepName}
                          backgroundColor={dbItem.stepColor}
                          textColor={getContrastYIQ(theme, dbItem.stepColor)}
                          component="span"
                        />
                      ) : (
                        <Chip label={t('common.unassigned')} component="span" />
                      ),
                      status: (
                        <ChipCustom
                          label={t(`cases.status.${dbItem.status}`)}
                          backgroundColor={theme.palette.cases[dbItem.status].bg}
                          textColor={theme.palette.cases[dbItem.status].text}
                          component="span"
                        />
                      ),
                      agent: dbItem.agentId ? (
                        <Link to={routes.dashboard.userView.replace(':id', String(dbItem.agentId))}>
                          {`${dbItem.agentFirstName} ${dbItem.agentLastName}`}
                        </Link>
                      ) : (
                        'N/A'
                      ),
                      lastUpdated: 'N/A',
                    })) || [],
                  totalCount: casesData?.count || 0,
                }}
                pagination={pagination}
                stickyHeader
              />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          {clientInfo}
        </Grid>
      </Grid>
    </Box>
  );
};
